import React, { useState, useEffect } from 'react';
import { Drawer, Form, Input, Button, message, List, Avatar, Divider, Spin } from 'antd';
import { UserOutlined, SearchOutlined, PlusOutlined, PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { customerV1Service, Customer } from '../../services/customerV1Service';

interface AddCustomerModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
}

const AddCustomerModalMobile: React.FC<AddCustomerModalMobileProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Tìm kiếm khách hàng
  const searchCustomers = async (query: string) => {
    if (!query.trim()) {
      setCustomers([]);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await customerV1Service.searchCustomers(query);
      setCustomers(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      message.error('Lỗi tìm kiếm khách hàng');
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => {
      searchCustomers(searchQuery);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const newCustomer = await customerV1Service.createCustomer({
        fullName: values.fullName,
        phoneNumber: values.phoneNumber,
        address: values.address || ''
      });

      message.success('Tạo khách hàng thành công!');
      onSuccess(newCustomer);
      form.resetFields();
      setShowCreateForm(false);
      setSearchQuery('');
      setCustomers([]);
    } catch (error: any) {
      message.error(error.message || 'Có lỗi xảy ra khi tạo khách hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowCreateForm(false);
    setSearchQuery('');
    setCustomers([]);
    onClose();
  };

  const handleSelectCustomer = (customer: Customer) => {
    onSuccess(customer);
    setSearchQuery('');
    setCustomers([]);
    onClose();
  };

  const handleCreateNew = () => {
    setShowCreateForm(true);
    setSearchQuery('');
    setCustomers([]);
  };

  return (
    <Drawer
      title="Quản lý khách hàng"
      placement="bottom"
      height="80%"
      open={isOpen}
      onClose={handleCancel}
      bodyStyle={{ padding: '16px' }}
      styles={{
        wrapper: {
          zIndex: 5000, // Cao hơn tất cả các element trong SalePageMobile
        },
        mask: {
          zIndex: 4999, // Cao hơn tất cả các element trong SalePageMobile
        },
        content: {
          zIndex: 5000, // Cao hơn tất cả các element trong SalePageMobile
        }
      }}
    >
      {!showCreateForm ? (
        // Tìm kiếm khách hàng
        <div>
          <div style={{ marginBottom: '16px' }}>
            <Input
              placeholder="Tìm kiếm khách hàng theo tên hoặc số điện thoại..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ marginBottom: '12px' }}
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreateNew}
              style={{ width: '100%' }}
            >
              Thêm khách hàng mới
            </Button>
          </div>

          {searchLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '8px' }}>Đang tìm kiếm...</p>
            </div>
          ) : customers.length > 0 ? (
            <List
              dataSource={customers}
              renderItem={(customer) => (
                <List.Item
                  onClick={() => handleSelectCustomer(customer)}
                  style={{ 
                    cursor: 'pointer', 
                    padding: '12px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={
                      <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                        {customer.fullName}
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                          <PhoneOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                          {customer.phoneNumber}
                        </div>
                        {customer.address && (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <EnvironmentOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />
                            {customer.address}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : searchQuery ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              <p>Không tìm thấy khách hàng</p>
              <Button type="link" onClick={handleCreateNew}>
                Tạo khách hàng mới
              </Button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              <p>Nhập từ khóa để tìm kiếm khách hàng</p>
            </div>
          )}
        </div>
      ) : (
        // Form tạo khách hàng mới
        <div>
          <div style={{ marginBottom: '16px' }}>
            <Button 
              type="link" 
              onClick={() => setShowCreateForm(false)}
              style={{ padding: 0 }}
            >
              ← Quay lại tìm kiếm
            </Button>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              fullName: '',
              phoneNumber: '',
              address: ''
            }}
          >
            <Form.Item
              name="fullName"
              label="Họ và tên"
              rules={[
                { required: true, message: 'Vui lòng nhập họ và tên' },
                { min: 2, message: 'Họ và tên phải có ít nhất 2 ký tự' }
              ]}
            >
              <Input placeholder="Nhập họ và tên khách hàng" />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label="Số điện thoại"
              rules={[
                { required: true, message: 'Vui lòng nhập số điện thoại' },
                { pattern: /^[0-9]{10,11}$/, message: 'Số điện thoại không hợp lệ' }
              ]}
            >
              <Input placeholder="Nhập số điện thoại" />
            </Form.Item>

            <Form.Item
              name="address"
              label="Địa chỉ"
            >
              <Input.TextArea 
                placeholder="Nhập địa chỉ (không bắt buộc)"
                rows={3}
              />
            </Form.Item>

            <Form.Item>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button onClick={handleCancel} style={{ flex: 1 }}>
                  Hủy
                </Button>
                <Button type="primary" htmlType="submit" loading={loading} style={{ flex: 1 }}>
                  Tạo khách hàng
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
      )}
    </Drawer>
  );
};

export default AddCustomerModalMobile;